import { inject, Injectable, Injector } from '@angular/core';
import { Resolve } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  EFieldTypes,
  ESqlOperators,
  IPersistentBoard,
  PersistentActivityStore,
  PersistentBoardStore,
  PersistentCommentTagStore,
  PersistentItemCategoryStore,
  PersistentJobStore,
  PersistentLineStore,
  PersistentShiftStore,
  PersistentSiteStore,
  PersistentStoreHelperService,
  PersistentTaskNameStore,
  PersistentWorkflowStepStore,
  PersistentWorkOrderStore,
  IAdvancedFilterConfiguration as WidgetAdvancedFilterConfiguration,
} from 'scw-digital-boards-widgets';
import { TReducerState } from '../../app.config';
import { CustomFilterService } from '../../core/services/custom-filter.service';
import {
  activityApiResource,
  boardApiResource,
  commentTagApiResource,
  customApiResource,
  itemCategoryApiResource,
  jobApiResource,
  lineApiResource,
  shiftApiResource,
  siteApiResource,
  taskNameApiResource,
  woApiResource,
  workflowStepApiResource,
} from '../../shared/components/advanced-filter/advanced-filter-api-resources';
import { getOperatorsForAdvancedFilterField } from '../../shared/components/advanced-filter/advanced-filter-fields.constants';
import { AdvancedFilterManager } from '../../shared/components/advanced-filter/advanced-filter-manager';
import {
  IAdvancedFilterApiValueConfig,
  IAdvancedFilterConfiguration,
  IAdvancedFilterCustomApiConfig,
  IAdvancedFilterCustomPredefinedConfig,
  IAdvancedFilterDateRangeConfig,
  IAdvancedFilterPredefinedConfig,
} from '../../shared/components/advanced-filter/advanced-filter.interface';
import { IWorkOrder } from '../../shared/components/form-association/form-association.interface';
import { IDropdownOption } from '../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../state/state.interface';
import {
  ECustomFilterType,
  IApiCustomFilterConfiguration,
  IPredefinedCustomFilterConfiguration,
} from '../settings/filter-settings/custom-filters/custom-filters.interface';
import {
  IActivity,
  IItemCategory,
  IJob,
  ILine,
  ISite,
  ITask,
  TWorkflowStep,
} from './widget-add-edit-dialog/widget-add-edit-dialog.interface';

@Injectable({
  providedIn: 'root',
})
export class DashboardContentAdvancedFilterResolver implements Resolve<AdvancedFilterManager> {
  private readonly store: Store<TReducerState> = inject(Store<TReducerState>);
  private readonly injector = inject(Injector);
  private readonly persistentStoreHelperService = inject(PersistentStoreHelperService);
  private readonly persistentTaskNameStore = inject(PersistentTaskNameStore);
  private readonly persistentCommentTagStore = inject(PersistentCommentTagStore);
  private readonly persistentShiftStore = inject(PersistentShiftStore);
  private readonly persistentLineStore = inject(PersistentLineStore);
  private readonly persistentSiteStore = inject(PersistentSiteStore);
  private readonly persistentWorkOrderStore = inject(PersistentWorkOrderStore);
  private readonly persistentItemCategoryStore = inject(PersistentItemCategoryStore);
  private readonly persistentActivityStore = inject(PersistentActivityStore);
  private readonly persistentBoardStore = inject(PersistentBoardStore);
  private readonly persistentJobStore = inject(PersistentJobStore);
  private readonly persistentWorkflowStepStore = inject(PersistentWorkflowStepStore);
  private readonly customFilterService = inject(CustomFilterService);
  private readonly areDefaultValuesReady$ = this.store.select(
    ({ dashboardContent }) => dashboardContent.dashboardDataLoadStatus === ELoadStatus.success,
  );
  private readonly defaultValues = this.store.selectSignal(
    ({ dashboardContent }) => dashboardContent.dashboardData?.defaultFilterCard ?? [],
  );

  public resolve(): Observable<AdvancedFilterManager> {
    return this.customFilterService.getMany({}).pipe(
      map(({ data: customFilters }) => {
        const lineFilter: IAdvancedFilterConfiguration<ILine> = {
          configurations: [
            {
              clientSideSearch: true,
              displayNameKey: 'title',
              matcherName: 'lineId',
              operators: getOperatorsForAdvancedFilterField(
                'lineId',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentLineStore,
              resource: lineApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'lineId',
        };

        const boardFilter: IAdvancedFilterConfiguration<IPersistentBoard> = {
          configurations: [
            {
              clientSideSearch: true,
              displayNameKey: 'name',
              matcherName: 'boardId',
              operators: getOperatorsForAdvancedFilterField(
                'boardId',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentBoardStore,
              resource: boardApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'boardId',
        };

        const itemCategoryFilter: IAdvancedFilterConfiguration<IItemCategory> = {
          configurations: [
            {
              clientSideSearch: true,
              displayNameKey: 'name',
              matcherName: 'itemCategoryId',
              operators: getOperatorsForAdvancedFilterField(
                'itemCategoryId',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentItemCategoryStore,
              resource: itemCategoryApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'itemCategoryId',
        };

        const statusCategoryFilter: IAdvancedFilterConfiguration = {
          configurations: [
            {
              dataKey: 'statusCategory',
              operators: getOperatorsForAdvancedFilterField(
                'statusCategory',
                EFieldTypes.predefined,
              ) as IAdvancedFilterPredefinedConfig['operators'],
              type: EFieldTypes.predefined,
            },
          ],
          field: 'statusCategory',
        };

        const workflowStepFilter: IAdvancedFilterConfiguration<TWorkflowStep> = {
          configurations: [
            {
              clientSideSearch: true,
              displayNameKey: 'name',
              matcherName: 'workflowStep',
              operators: getOperatorsForAdvancedFilterField(
                'workflowStep',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentWorkflowStepStore,
              resource: workflowStepApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'workflowStep',
        };

        const decisionDateFilter: IAdvancedFilterConfiguration = {
          configurations: [
            {
              dataKey: 'decisionDateType',
              operators: getOperatorsForAdvancedFilterField(
                'decisionDateType',
                EFieldTypes.predefined,
              ) as IAdvancedFilterPredefinedConfig['operators'],
              type: EFieldTypes.predefined,
            },
          ],
          field: 'decisionDateType',
        };

        const actionItemTypeFilter: IAdvancedFilterConfiguration = {
          configurations: [
            {
              dataKey: 'actionItemType',
              operators: getOperatorsForAdvancedFilterField(
                'actionItemType',
                EFieldTypes.predefined,
              ) as IAdvancedFilterPredefinedConfig['operators'],
              type: EFieldTypes.predefined,
            },
          ],
          field: 'actionItemType',
        };

        const siteFilter: IAdvancedFilterConfiguration<ISite> = {
          configurations: [
            {
              clientSideSearch: true,
              displayNameKey: 'name',
              matcherName: 'siteIds',
              operators: getOperatorsForAdvancedFilterField(
                'siteIds',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentSiteStore,
              resource: siteApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'siteIds',
        };

        const jobFilter: IAdvancedFilterConfiguration<IJob> = {
          configurations: [
            {
              displayNameKey: 'jobName',
              matcherName: 'jobId',
              operators: getOperatorsForAdvancedFilterField(
                'jobId',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentJobStore,
              resource: jobApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'jobId',
        };

        const dateRangeFilter: IAdvancedFilterConfiguration = {
          configurations: [
            {
              operators: getOperatorsForAdvancedFilterField(
                'date',
                EFieldTypes.date,
              ) as IAdvancedFilterDateRangeConfig['operators'],
              type: EFieldTypes.date,
            },
          ],
          field: 'date',
        };

        const shiftFilter: IAdvancedFilterConfiguration<IDropdownOption> = {
          configurations: [
            {
              clientSideSearch: true,
              displayNameKey: 'name',
              matcherName: 'shiftId',
              operators: getOperatorsForAdvancedFilterField(
                'shiftId',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentShiftStore,
              resource: shiftApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'shiftId',
        };

        const workOrderFilter: IAdvancedFilterConfiguration<IWorkOrder> = {
          configurations: [
            {
              displayNameKey: 'woNumber',
              matcherName: 'workOrderId',
              operators: getOperatorsForAdvancedFilterField(
                'workOrderId',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentWorkOrderStore,
              resource: woApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'workOrderId',
        };

        const activityFilter: IAdvancedFilterConfiguration<IActivity> = {
          configurations: [
            {
              displayNameKey: 'name',
              matcherName: 'activityId',
              operators: getOperatorsForAdvancedFilterField(
                'activityId',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentActivityStore,
              resource: activityApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'activityId',
        };

        const taskNameFilter: IAdvancedFilterConfiguration<ITask> = {
          configurations: [
            {
              displayNameKey: 'title',
              matcherName: 'taskName',
              operators: getOperatorsForAdvancedFilterField(
                'taskName',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              // @ts-ignore
              persistentStore: this.persistentTaskNameStore,
              resource: taskNameApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'taskName',
        };

        const commentTagFilter: IAdvancedFilterConfiguration<IDropdownOption> = {
          configurations: [
            {
              clientSideSearch: true,
              displayNameKey: 'name',
              matcherName: 'commentTag',
              // @ts-ignore
              operators: getOperatorsForAdvancedFilterField(
                'commentTag',
                EFieldTypes.apiDropdown,
              ) as IAdvancedFilterApiValueConfig['operators'],
              persistentStore: this.persistentCommentTagStore,
              resource: commentTagApiResource(this.injector),
              type: EFieldTypes.apiDropdown,
            },
          ],
          field: 'commentTag',
        };

        const customApiFilters = customFilters
          .filter(({ typeBasedConfiguration }) => typeBasedConfiguration.type === ECustomFilterType.external)
          .map(({ id, name, typeBasedConfiguration }) => {
            return {
              configurations: [
                {
                  idOfCustom: id,
                  operators: [ESqlOperators.$in],
                  resource: customApiResource(this.injector, typeBasedConfiguration as IApiCustomFilterConfiguration),
                  type: EFieldTypes.customApiDropdown,
                },
              ] satisfies IAdvancedFilterCustomApiConfig[],
              field: name,
              idOfCustom: id,
            };
          });

        const customPredefinedFilters = customFilters
          .filter(({ typeBasedConfiguration }) => typeBasedConfiguration.type === ECustomFilterType.manual)
          .map(({ id, name, typeBasedConfiguration }) => {
            return {
              configurations: [
                {
                  idOfCustom: id,
                  operators: [ESqlOperators.$in],
                  type: EFieldTypes.customPredefined,
                  values: (typeBasedConfiguration as IPredefinedCustomFilterConfiguration).values.map((value) => ({
                    id: value.id ?? value.name,
                    name: value.name,
                  })),
                },
              ] satisfies IAdvancedFilterCustomPredefinedConfig[],
              field: name,
              idOfCustom: id,
            };
          });

        const configurations = [
          lineFilter,
          boardFilter,
          itemCategoryFilter,
          actionItemTypeFilter,
          decisionDateFilter,
          siteFilter,
          jobFilter,
          statusCategoryFilter,
          workflowStepFilter,
          dateRangeFilter,
          shiftFilter,
          workOrderFilter,
          activityFilter,
          taskNameFilter,
          commentTagFilter,
          ...customApiFilters,
          ...customPredefinedFilters,
        ];

        this.persistentStoreHelperService.advancedFilterConfiguration =
          configurations as WidgetAdvancedFilterConfiguration[];

        return new AdvancedFilterManager(
          configurations as IAdvancedFilterConfiguration[],
          this.areDefaultValuesReady$,
          this.defaultValues,
        );
      }),
    );
  }
}
